import { useRef, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { UploadType } from "../../types/enum";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

function TabPanel({ children, value, index }) {
  return (
    <Box hidden={value !== index} p={2} pt={8}>
      {children}
    </Box>
  );
}

export default function FileUploadDialog({
  open,
  handleClose,
  isUploading,
  handleImageUpload,
  title,
  initialImageLink = "",
}) {
  const visuallyHiddenRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedImage, setSelectedImage] = useState({
    uploadType: "",
    imageLink: initialImageLink,
    imageFile: null,
  });
  const handleChange = (event, newValue) => {
    const uploadType = newValue === 0 ? UploadType.FILE : UploadType.LINK;
    setSelectedImage((prev) => ({ ...prev, uploadType: uploadType }));
    setTabValue(newValue);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const imageLink = formData.get("imageLink");
    const imageFile = formData.get("imageFile");
    const uploadType = tabValue === 0 ? UploadType.FILE : UploadType.LINK;
    await handleImageUpload({ uploadType, imageFile, imageLink });
  };

  const listOfTabs = [
    {
      uploadType: UploadType.FILE,
      label: `File`,
      content: (
        <div>
          {selectedImage.imageFile?.name && (
            <Box display="flex" gap={1}>
              <Typography variant="caption" gutterBottom sx={{ display: "block" }}>
                {selectedImage.imageFile?.name}
              </Typography>
              <IconButton
                sx={{ mt: -1 }}
                onClick={() => {
                  setSelectedImage((prev) => ({ ...prev, imageFile: null }));
                  if (visuallyHiddenRef.current) {
                    visuallyHiddenRef.current.value = null;
                  }
                }}
                aria-label="delete"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Button
            startIcon={<CloudUploadIcon />}
            size="small"
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
          >
            Browse file
            <VisuallyHiddenInput
              ref={visuallyHiddenRef}
              type="file"
              id="imageFile"
              name="imageFile"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                setSelectedImage((prev) => ({ ...(prev || {}), imageFile: file }));
              }}
            />
          </Button>
        </div>
      ),
    },
    {
      uploadType: UploadType.LINK,
      label: `Link`,
      content: (
        <TextField
          label="Provide an image link"
          value={selectedImage.imageLink}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedImage((prev) => ({
              ...prev,
              imageLink: value,
            }));
          }}
          variant="standard"
          color="info"
          name="imageLink"
          id="imageLink"
          fullWidth
          focused
        />
      ),
    },
  ];

  useEffect(() => {
    if (open) {
      setSelectedImage({ uploadType: "", imageFile: null, imageLink: initialImageLink });
      setTabValue(0);
    }
  }, [open, initialImageLink]);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} id="subscription-form">
            <Box sx={{ width: "100%" }}>
              <Tabs value={tabValue} onChange={handleChange} aria-label="icon position tabs">
                {listOfTabs.map((t, index) => (
                  <Tab key={`upload_tab_id_${index}`} label={t.label}></Tab>
                ))}
              </Tabs>
              {listOfTabs.map((t, index) => (
                <TabPanel key={`upload_tab_panel_id_${index}`} value={tabValue} index={index}>
                  {t.content}
                </TabPanel>
              ))}
            </Box>
          </form>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            loading={isUploading}
            size="small"
            disabled={(!selectedImage?.imageFile && !selectedImage?.imageLink) || isUploading}
            type="submit"
            form="subscription-form"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
